import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { Col, Row } from "react-bootstrap";

import { getCountries } from "api";
import errorToast from "helpers/errorToast";
import testIds from "constants/testIds";
import Button from "components/atoms/Button/Button";
import FormField from "components/molecules/FormField/FormField";
import { ICountries } from "pages/shipper/RequestForm/components/Schedule/Types";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";

type BillingInfoAddressV1Props = {
  onSubmit: () => void;
  onClose: () => void;
};

function BillingInfoAddressV1(props: BillingInfoAddressV1Props) {
  const { onSubmit, onClose } = props;
  const [countries, setCountries] = useState<ICountries[]>([]);

  const intl = useIntl();
  const methods = useFormContext();
  const { getValues, setValue } = methods;

  useEffect(() => {
    getCountries()
      .then((res) => {
        // For now we have to manually remove the country with label "USA" as its causing errors.
        const filteredCountries = res.data.filter((country: ICountries) => country.label !== "USA");
        setCountries(filteredCountries);
      })
      .catch((error) => errorToast(error));
  }, []);

  return (
    <div>
      <Row>
        <Col md={6} lg={6}>
          <h6>
            <FormattedMessage id="billing.billedFrom" defaultMessage="Billed from " />:
          </h6>
          <div className="w-192 mt-3">
            <FormField
              label={intl.formatMessage({
                id: "general.companyAndAddress",
                defaultMessage: "Company/Address",
              })}
              type="textinput"
              placeholder={intl.formatMessage({
                id: "general.typeHere",
                defaultMessage: "Type here..",
              })}
              field_id="invoice.billed_from.company"
              mandatory
              onChange={(e) => {
                setValue("invoice.billed_from.company", e.target.value);
              }}
            />
          </div>
          <div className="w-192 mt-4">
            <FormField
              label={intl.formatMessage({
                id: "general.address",
                defaultMessage: "Address",
              })}
              type="textinput"
              placeholder={intl.formatMessage({
                id: "general.typeHere",
                defaultMessage: "Type here..",
              })}
              field_id="invoice.billed_from.address"
              mandatory
              onChange={(e) => {
                setValue("invoice.billed_from.address", e.target.value);
              }}
            />
          </div>
          <div className="w-192 mt-4">
            <FormField
              label={intl.formatMessage({
                id: "general.city",
                defaultMessage: "City",
              })}
              type="textinput"
              placeholder={intl.formatMessage({
                id: "general.typeHere",
                defaultMessage: "Type here..",
              })}
              field_id="invoice.billed_from.city"
              onChange={(e) => {
                setValue("invoice.billed_from.city", e.target.value);
              }}
            />
          </div>
          <div className="w-192 mt-4 billing-info">
            <SearchSelect
              name="invoice.billed_from.country"
              defaultValue={getValues("invoice.billed_from.country")}
              label={intl.formatMessage({
                id: "general.country",
                defaultMessage: "Country",
              })}
              placeholder={intl.formatMessage({
                id: "general.country",
                defaultMessage: "Country",
              })}
              options={countries}
              onSelect={(item: any) => {
                setTimeout(() => {
                  setValue("invoice.billed_from.country", item?.[0]?.label || "");
                }, 0);
              }}
              required
            />
          </div>
        </Col>
        <Col md={6} lg={6}>
          <h6>
            <FormattedMessage id="billing.billedTo" defaultMessage="Billed to:" />
          </h6>
          <div className="w-192 mt-3">
            <FormField
              label={intl.formatMessage({
                id: "general.companyAndAddress",
                defaultMessage: "Company/Address",
              })}
              type="textinput"
              placeholder={intl.formatMessage({
                id: "general.typeHere",
                defaultMessage: "Type here..",
              })}
              field_id="invoice.billed_to.company"
              mandatory
              onChange={(e) => {
                setValue("invoice.billed_to.company", e.target.value);
              }}
            />
          </div>
          <div className="w-192 mt-4">
            <FormField
              label={intl.formatMessage({
                id: "general.address",
                defaultMessage: "Address",
              })}
              type="textinput"
              placeholder={intl.formatMessage({
                id: "general.typeHere",
                defaultMessage: "Type here..",
              })}
              field_id="invoice.billed_to.address"
              mandatory
              onChange={(e) => {
                setValue("invoice.billed_to.address", e.target.value);
              }}
            />
          </div>
          <div className="w-192 mt-4">
            <FormField
              label={intl.formatMessage({
                id: "general.city",
                defaultMessage: "City",
              })}
              type="textinput"
              placeholder={intl.formatMessage({
                id: "general.typeHere",
                defaultMessage: "Type here..",
              })}
              field_id="invoice.billed_to.city"
              onChange={(e) => {
                setValue("invoice.billed_to.city", e.target.value);
              }}
            />
          </div>
          <div className="w-192 mt-4 billing-info">
            <SearchSelect
              name="invoice.billed_to.country"
              defaultValue={getValues("invoice.billed_to.country")}
              label={intl.formatMessage({
                id: "general.country",
                defaultMessage: "Country",
              })}
              placeholder={intl.formatMessage({
                id: "general.country",
                defaultMessage: "Country",
              })}
              options={countries}
              onSelect={(item: any) => {
                setTimeout(() => {
                  setValue("invoice.billed_to.country", item?.[0]?.label || "");
                }, 0);
              }}
              required
            />
          </div>
        </Col>
      </Row>
      <Row className="mt-5">
        <Col lg={12} className="d-flex justify-content-between">
          <Button
            variant="outline-primary"
            label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
            size="lg"
            onClick={onClose}
            data-testid={testIds.SHIPMENT_DASHBOARD_UNDECLINE_MODAL_CLOSE_BUTTON}
          />
          <Button
            variant="primary"
            type="button"
            size="lg"
            label={intl.formatMessage({ id: "general.save", defaultMessage: "Save" })}
            data-testid={testIds.SHIPMENT_DASHBOARD_UNDECLINE_UNDO_BUTTON}
            onClick={onSubmit}
          />
        </Col>
      </Row>
    </div>
  );
}

export default BillingInfoAddressV1;
